<template>
    <div class="Chat" v-if="discussion">
        <div class="Chat__head">
            <button class="Chat__head-contacts" @click.prevent="$emit('openPanel', 'contacts')">
                <i class="fas fa-users"></i>
            </button>
            <h4 class="Chat__head-title truncate">{{ name }}</h4>
            <button class="Chat__head-alerts" @click.prevent="$emit('openPanel', 'alerts')">
                <i class="fas fa-info"></i>
            </button>
        </div>
        <div v-if="loading" class="Chat__loader">
            <i class="fas fa-spinner fa-spin"></i>
        </div>
        <ul class="Chat__messages" ref="messages">
            <li v-for="message in messages" :class="{
                'ChatMessage': true,
                'ChatMessage--mine': message.user.id === user.id
            }">
                <img v-if="message.user.id !== user.id"
                     class="ChatMessage__avatar"
                     :src="message.user.profile.small_avatar"
                     :alt="message.user.profile.name"/>
                <div>
                    <div class="ChatMessage__body">
                        <p class="ChatMessage__content">{{ message.content }}</p>
                        <span class="ChatMessage__date">{{ chatFormatTime(message.created_at) }}</span>
                    </div>
                    <button
                        v-if="message.status === 'failed'"
                        class="ChatMessage__retry"
                        type="button"
                        @click.prevent="sendMessage(message.content, message.id)">
                        <i class="fas fa-exclamation-circle m-r-5"></i> Réessayer
                    </button>
                </div>
            </li>
        </ul>
        <chat-form :loading="false" @submit="sendMessage"/>
    </div>
</template>

<script>
    import NewDiscussion from "./NewDiscussion";
    import Discussion from "./Discussion";
    import {actions, getters, mutations} from "../../store";
    import {chatFormatTime} from "../../utils";
    import Api from "../../services/Api";
    import {v4 as uuidv4} from 'uuid';
    import ChatForm from "./ChatForm";

    export default {
        components: {ChatForm, NewDiscussion, Discussion},
        data: function () {
            return {
                typedMessage: '',
                loading: false,
                originalScrollHeight: null,
                lastScrollTop: null,
                hasOldestMessages: true
            }
        },
        computed: {
            user: getters.user,
            discussion: getters.openedDiscussion,
            discussionId: getters.openedDiscussionId,
            messages: function () {
                return getters.discussionMessages(this.discussionId);
            },
            name: function () {
                return getters.discussionName(this.discussionId);
            }
        },
        watch: {
            discussionId: function (newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.opened();
                }
            },
        },
        methods: {
            chatFormatTime,
            fetchOldestMessages: async function () {
                this.loading = true;

                const oldestMessage = getters.discussionMessages(this.discussion.id).shift();
                const res = await actions.fetchMessages(this.discussion.id, {before: oldestMessage._cursor});
                this.hasOldestMessages = !!res.meta.next;

                this.loading = false;
            },
            opened: async function () {
                this.loading = true;
                const res = await actions.fetchMessages(this.discussionId);
                this.hasOldestMessages = res.data.length > 0;
                this.loading = false;

                this.scrollToBottom(false);

                await actions.markAsRead(this.discussionId);
            },
            scrollToBottom: function (animated = true) {
                let element = this.$refs.messages;
                element.scrollTo({
                    left: 0,
                    top: element.scrollHeight,
                    behavior: animated ? 'smooth' : 'auto'
                });
            },
            sendMessage: async function (message, id = null,) {
                const entity = {
                    id: id ?? uuidv4(),
                    content: message,
                    discussion_id: this.discussionId,
                    status: 'waiting',
                    user: this.user,
                    created_at: Date.now() / 1000,
                    updated_at: Date.now() / 1000
                };

                try {
                    mutations.pushDiscussions({
                        ...this.discussion,
                        updated_at: entity.created_at,
                        users_reads: {
                            ...this.discussion.users_reads,
                            [this.user.id]: entity.created_at
                        },
                        last_message: entity
                    });

                    setTimeout(() => this.scrollToBottom(), 100);

                    const {data} = await Api.post(`/discussions/${this.discussionId}/messages`, {
                        id: entity.id,
                        content: entity.content
                    });

                    mutations.pushDiscussions({
                        ...this.discussion,
                        updated_at: data.created_at,
                        last_message: data,
                        users_reads: {
                            ...this.discussion.users_reads,
                            [this.user.id]: data.created_at
                        }
                    });
                } catch (err) {
                    mutations.pushDiscussions({
                        ...this.discussion,
                        updated_at: entity.created_at,
                        last_message: {
                            ...entity,
                            status: 'failed',
                        }
                    });
                }
            },
            handleScroll: async function (e) {
                let {scrollTop, scrollHeight} = e.target;
                this.originalScrollHeight = scrollHeight;
                if ((!this.lastScrollTop || this.lastScrollTop > scrollTop) && scrollTop < 50 && !this.loading && this.hasOldestMessages) {
                    await this.fetchOldestMessages();
                    e.target.scrollTop += (scrollHeight - this.originalScrollHeight) + 50;
                }
            }
        },
        mounted() {
            this.opened();
            this.$refs.messages.addEventListener('scroll', this.handleScroll);
        },
        beforeDestroy() {
            this.$refs.messages.removeEventListener('scroll', this.handleScroll);
        }
    }
</script>
